import type { CreateAddressBookEntryData, AddressBookEntry, AddressBookFilters, UpdateAddressBookEntryData } from "@/types/address-book";

// Mock data per address book entries
const mockAddressBookEntries: AddressBookEntry[] = [
    // Address Book per TechCorp Solutions LLC (accountId: "1")
    {
        id: "1",
        accountId: "1",
        firstName: "Marco",
        lastName: "Rossi",
        email: "marco.rossi@autorita.it",
        role: "Autorità",
    },
    {
        id: "2",
        accountId: "1",
        firstName: "Giulia",
        lastName: "Bianchi",
        email: "giulia.bianchi@freightforwarder.com",
        role: "Freight Forwarder",
    },
    {
        id: "3",
        accountId: "1",
        firstName: "Luca",
        lastName: "Verdi",
        email: "luca.verdi@sales.com",
        role: "Sales",
    },
    {
        id: "4",
        accountId: "1",
        firstName: "Sofia",
        lastName: "Neri",
        email: "sofia.neri@spedizioniere.it",
        role: "Spedizioniere",
    },
    {
        id: "5",
        accountId: "1",
        firstName: "Andrea",
        lastName: "Ferrari",
        email: "andrea.ferrari@dogana.it",
        role: "Dogana",
    },
    {
        id: "6",
        accountId: "1",
        firstName: "Elena",
        lastName: "Romano",
        email: "elena.romano@vettoremarittimo.it",
        role: "Vettore marittimo",
    },
    {
        id: "7",
        accountId: "1",
        firstName: "Davide",
        lastName: "Colombo",
        email: "davide.colombo@trasporto.it",
        role: "Trasporto terrestre",
    },
    {
        id: "ff-demo-1",
        accountId: "1",
        firstName: "Global Logistics",
        lastName: "Forwarding",
        email: "info@globallogistics.it",
        role: "Freight Forwarder",
    },

    // Address Book per Innovation Hub Partnership (accountId: "3")
    {
        id: "8",
        accountId: "3",
        firstName: "Hans",
        lastName: "Mueller",
        email: "hans.mueller@porto-imbarco.de",
        role: "Porto imbarco",
    },
    {
        id: "9",
        accountId: "3",
        firstName: "Anna",
        lastName: "Schmidt",
        email: "anna.schmidt@porto-sbarco.de",
        role: "Porto di sbarco",
    },
    {
        id: "10",
        accountId: "3",
        firstName: "Thomas",
        lastName: "Weber",
        email: "thomas.weber@aeroporto.de",
        role: "Staff aeroporto",
    },

    // Address Book per Receiver Account (accountId: "2")
    {
        id: "11",
        accountId: "2",
        firstName: "Paolo",
        lastName: "Esposito",
        email: "paolo.esposito@autorita.it",
        role: "Autorità",
    },
    {
        id: "12",
        accountId: "2",
        firstName: "Francesca",
        lastName: "Ricci",
        email: "francesca.ricci@freightforwarder.com",
        role: "Freight Forwarder",
    },
    {
        id: "13",
        accountId: "2",
        firstName: "Matteo",
        lastName: "Marino",
        email: "matteo.marino@sales.com",
        role: "Sales",
    },
    // Address Book per Global Logistics Forwarding (accountId: "ff-1")
    {
        id: "14",
        accountId: "ff-1",
        firstName: "Chiara",
        lastName: "Romani",
        email: "chiara.romani@globallogistics.it",
        role: "Freight Forwarder",
    },
    {
        id: "15",
        accountId: "ff-1",
        firstName: "Paolo",
        lastName: "Gallo",
        email: "paolo.gallo@armatoriazzurra.com",
        role: "Armatore",
    },
    {
        id: "16",
        accountId: "ff-1",
        firstName: "Isabella",
        lastName: "Mariani",
        email: "isabella.mariani@utilityenergy.eu",
        role: "Trasporto terrestre",
    },
];

// In-memory storage
let addressBookEntries = [...mockAddressBookEntries];

// Helper functions per CRUD operations
export const addressBookService = {
    // Get all address book entries
    getAll: (): AddressBookEntry[] => {
        return [...addressBookEntries];
    },

    // Get address book entry by ID
    getById: (id: string): AddressBookEntry | undefined => {
        return addressBookEntries.find((entry) => entry.id === id);
    },

    // Get address book entries by account ID
    getByAccountId: (accountId: string): AddressBookEntry[] => {
        return addressBookEntries.filter((entry) => entry.accountId === accountId);
    },

    // Create new address book entry
    create: (data: CreateAddressBookEntryData): AddressBookEntry => {
        const newEntry: AddressBookEntry = {
            id: (addressBookEntries.length + 1).toString(),
            ...data,
        };
        addressBookEntries.push(newEntry);
        return newEntry;
    },

    // Update address book entry
    update: (id: string, data: UpdateAddressBookEntryData): AddressBookEntry | null => {
        const index = addressBookEntries.findIndex((entry) => entry.id === id);
        if (index === -1) return null;

        addressBookEntries[index] = {
            ...addressBookEntries[index],
            ...data,
        };
        return addressBookEntries[index];
    },

    // Delete address book entry
    delete: (id: string): boolean => {
        const index = addressBookEntries.findIndex((entry) => entry.id === id);
        if (index === -1) return false;

        addressBookEntries.splice(index, 1);
        return true;
    },

    // Search and filter address book entries
    search: (filters: AddressBookFilters): AddressBookEntry[] => {
        let filtered = [...addressBookEntries];

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter(
                (entry) =>
                    entry.firstName.toLowerCase().includes(searchTerm) ||
                    entry.lastName.toLowerCase().includes(searchTerm) ||
                    entry.email.toLowerCase().includes(searchTerm)
            );
        }

        if (filters.role) {
            filtered = filtered.filter((entry) => entry.role === filters.role);
        }

        if (filters.accountId) {
            filtered = filtered.filter((entry) => entry.accountId === filters.accountId);
        }

        return filtered;
    },
};

